import { IN_COMPETENCE, liquidoDe, reduzirNaProporcao } from '@domain/finance'
import type { Category, Transaction } from '@domain/finance'
import type { Cents } from '@shared/money/cents'

/**
 * "Gastos por categoria (competência)" — a rosca de
 * `Docs/04-Design/Telas/Dashboard.md`.
 *
 * *"Só categorias de primeiro nível; toque em uma fatia abre as filhas. Tudo
 * abaixo de 5% colapsa em 'Outros' — uma rosca com onze fatias não comunica
 * nada."*
 *
 * ## O que entra, e por quê
 *
 * - **Só a competência**, pelo `IN_COMPETENCE` — é a pergunta "quanto
 *   consumimos", e o pagamento da fatura (`settlement`) não é consumo.
 * - **Só saída.** Uma rosca que somasse o salário junto teria uma fatia
 *   "Salário" de 60% num gráfico chamado "gastos". Entrada some daqui e vive nos
 *   números do topo.
 * - **`transfer` fica de fora**, como em todo relatório de gasto
 *   (`Docs/03-Arquitetura/Competência e caixa.md`) — inclusive os 266
 *   movimentos de investimento da base real (decisão 139).
 * - **`canceled` fica de fora**, pelo mesmo motivo dos totais.
 *
 * ## "Sem categoria" é uma fatia, e não um descarte
 *
 * Na base real são **2.462 de 2.728** transações sem categoria. Escondê-las
 * daria uma rosca bonita sobre 10% do dinheiro, com os percentuais de todas as
 * outras fatias **errados** — cada uma pareceria muito maior do que é. A fatia
 * existe, é a maior, e é o chamado à ação da tela.
 *
 * ## A rosca soma pelos ITENS do breakdown, quando eles existem
 *
 * Uma compra de mercado detalhada em três categorias aparece **nas três**, cada
 * uma com o valor do item — e o **resto** vai para a categoria da própria
 * transação. É exatamente o invariante de [[Breakdown]] desenhado como gráfico:
 * `soma(itens) + resto ≡ |valor|`, então o total da rosca não muda, só a
 * distribuição.
 *
 * **Os itens não são lidos aqui.** `enrichment.breakdown.byCategory` já traz a
 * divisão, mantida por `onBreakdownItemWrite` — sem isso a tela que mais abre
 * precisaria de uma leitura por transação, noventa e uma no maior mês da base.
 * E o mapa **não inclui o resto** de propósito: a categoria da transação muda
 * quando alguém recategoriza, sem que item nenhum seja escrito, e um mapa que já
 * o somasse envelheceria em silêncio. Quem junta as duas coisas é esta função.
 *
 * ## O rollup é até o ancestral de PRIMEIRO nível
 *
 * Um gasto em "Feira" (neta) conta para "Alimentação" (raiz). A subida é por
 * `parentId` — não por `path`, que não está cunhado numa categoria recém-criada
 * pelo cliente (mesma razão do filtro de categoria, decisão 146d).
 *
 * ## O corte de 5% é sobre o TOTAL, não sobre a maior fatia
 *
 * "Abaixo de 5%" quer dizer 5% do total da rosca. Medir contra a maior fatia
 * faria o corte mudar sozinho quando a maior crescesse, e categorias
 * apareceriam e sumiriam sem nada ter acontecido com elas.
 */

export type Slice = {
  /** `null` é a fatia "Sem categoria"; `'outros'` é o agregado. */
  id: string | null
  name: string
  /** Soma **em módulo** — a rosca desenha grandezas. */
  cents: Cents
  /** 0..100, arredondado a uma casa. */
  percent: number
}

export type Shares = {
  /** A soma das fatias, em módulo. É o número do centro da rosca. */
  totalCents: Cents
  slices: Slice[]
}

/** Abaixo disto a fatia vira "Outros". */
const CORTE = 5

/** A raiz da árvore de quem tem `id`, subindo por `parentId`. */
function rootOf(categoryId: string, byId: Map<string, Category>): Category | null {
  const visto = new Set<string>()
  let atual = byId.get(categoryId)
  while (atual !== undefined && atual.parentId !== null) {
    // A árvore pode receber um ciclo — `wouldCreateCycle` existe por isso. Sem
    // o `visto`, a rosca travaria a tela inteira.
    if (visto.has(atual.id)) return null
    visto.add(atual.id)
    atual = byId.get(atual.parentId)
  }
  return atual ?? null
}

/**
 * Os gastos do mês agrupados pela raiz da categoria.
 *
 * `parentId` opcional: quando passado, agrupa pelos **filhos diretos** dele em
 * vez das raízes — é o "toque em uma fatia abre as filhas". Um gasto lançado no
 * próprio pai vira a fatia "Direto em <pai>", porque somá-lo a um dos filhos
 * seria inventar de qual.
 */
/**
 * Só o que passou no CARTÃO DE CRÉDITO.
 *
 * É a pergunta que o dono faz no fim do mês — *"quanto foi de cartão, e em
 * quê"* —, e ela não é a mesma de "quanto gastamos": a compra no débito e o
 * boleto do condomínio não estão na fatura. O recorte é pelo instrumento, e não
 * pelo `cashImpact`: uma compra parcelada e uma à vista no cartão são as duas
 * `deferred`, mas o que define "é do cartão" é de onde ela saiu.
 */
export function noCartao(transactions: Transaction[]): Transaction[] {
  return transactions.filter((t) => t.source.instrument.type === 'creditCard')
}

export type AnelDuplo = {
  totalCents: Cents
  /** O anel de dentro: as categorias de primeiro nível. */
  pais: Slice[]
  /** O anel de fora, por pai. Pai que não subdivide não entra. */
  subsPorPai: Record<string, Slice[]>
}

/**
 * Os dois anéis de uma vez: pais, e as filhas de cada pai.
 *
 * **As filhas saem da MESMA função da rosca de primeiro nível**, com o pai como
 * recorte — inclusive o "Direto em <pai>" e o corte de 5%. Duas contas
 * diferentes para o mesmo dinheiro divergiriam, e o anel de fora deixaria de
 * fechar com a fatia que o sustenta.
 *
 * **O que não entra é o pai que não subdivide de verdade**: quando a única
 * fatia é o "Direto em <pai>", o anel externo seria idêntico ao interno,
 * dizendo "Alimentação é toda Alimentação". Uma filha só — todo o gasto de
 * Alimentação foi em Mercado — entra: o nome dela é informação que a fatia do
 * pai não carrega.
 */
export function anelDuplo(transactions: Transaction[], categories: Category[]): AnelDuplo {
  const { totalCents, slices } = categoryShares(transactions, categories)
  const subsPorPai: Record<string, Slice[]> = {}
  for (const pai of slices) {
    if (pai.id === null || pai.id === 'outros') continue
    const subs = categoryShares(transactions, categories, pai.id).slices
    const soDireto = subs.length === 1 && subs[0]?.id === pai.id
    if (subs.length > 0 && !soDireto) subsPorPai[pai.id] = subs
  }
  return { totalCents, pais: slices, subsPorPai }
}

export function categoryShares(
  transactions: Transaction[],
  categories: Category[],
  parentId: string | null = null,
): Shares {
  const byId = new Map(categories.map((category) => [category.id, category]))
  const pai = parentId === null ? null : byId.get(parentId)

  const somas = new Map<string | null, { name: string; cents: number }>()
  let total = 0

  for (const transaction of transactions) {
    if (transaction.status === 'canceled') continue
    if (!IN_COMPETENCE[transaction.cashImpact]) continue
    // Só saída. O sinal mora em `amountCents` e é lido aqui sem conversão.
    if (transaction.source.amountCents >= 0) continue

    for (const { categoryId, cents } of contribuicoes(transaction)) {
      const categoria = categoryId === null ? null : byId.get(categoryId)
      // A exclusão de transferência passou a ser por CONTRIBUIÇÃO, e não pela
      // transação inteira. Um item de Alimentação numa compra cuja transação
      // está em "Entre contas" conta; o resto, que é transferência, não. Antes
      // a transação inteira sumia, item e tudo.
      if (categoria?.kind === 'transfer') continue

      const chave = bucketOf(categoria ?? null, pai ?? null, byId)
      if (chave === undefined) continue

      total += cents
      const atual = somas.get(chave.id)
      somas.set(chave.id, { name: chave.name, cents: (atual?.cents ?? 0) + cents })
    }
  }

  return colapsar(somas, total)
}

/**
 * Quanto cada categoria recebe **desta** transação, em módulo.
 *
 * Os itens vêm do `byCategory`; o **resto** vai para a categoria da própria
 * transação. Sem item nenhum — o caso normal e o mais comum — é uma contribuição
 * só, com o valor inteiro, que é o que a rosca fazia antes de existir breakdown.
 *
 * **A soma das contribuições é `|amountCents|`, sempre**, porque é o invariante
 * de `breakdownRemainder` reescrito de outro jeito. Há caso afirmando isso.
 */
function contribuicoes(
  transaction: Transaction,
): { categoryId: string | null; cents: number }[] {
  const { breakdown, categoryId } = transaction.enrichment
  const dosItens = Object.entries(breakdown.byCategory).map(([id, cents]) => ({
    categoryId: id,
    cents,
  }))

  // Resto zero não vira contribuição: uma fatia de R$ 0,00 não desenha nada e
  // ainda apareceria na legenda.
  const inteiras =
    breakdown.remainderCents > 0
      ? [...dosItens, { categoryId, cents: breakdown.remainderCents }]
      : dosItens

  // Abatida, a compra vale o líquido, e cada categoria cai na MESMA proporção
  // — a soma continua fechando, agora no líquido. Ver `reduzirNaProporcao`.
  return transaction.enrichment.offset === null
    ? inteiras
    : reduzirNaProporcao(inteiras, -liquidoDe(transaction))
}

/**
 * Em qual fatia esta transação cai. `undefined` é "não pertence a esta rosca" —
 * o caso de estar drilhando "Alimentação" e a transação ser de "Transporte".
 */
function bucketOf(
  categoria: Category | null,
  pai: Category | null,
  byId: Map<string, Category>,
): { id: string | null; name: string } | undefined {
  if (categoria === null) {
    // Sem categoria só aparece na rosca de primeiro nível: dentro de
    // "Alimentação" ela não é uma filha de nada.
    return pai === null ? { id: null, name: 'Sem categoria' } : undefined
  }

  if (pai === null) {
    const raiz = rootOf(categoria.id, byId)
    return raiz === null ? undefined : { id: raiz.id, name: raiz.name }
  }

  if (categoria.id === pai.id) return { id: pai.id, name: `Direto em ${pai.name}` }
  const filho = ancestorUnder(categoria, pai.id, byId)
  return filho === null ? undefined : { id: filho.id, name: filho.name }
}

/** O filho direto de `parentId` que é ancestral de (ou é) `categoria`. */
function ancestorUnder(
  categoria: Category,
  parentId: string,
  byId: Map<string, Category>,
): Category | null {
  const visto = new Set<string>()
  let atual: Category | undefined = categoria
  while (atual !== undefined) {
    if (visto.has(atual.id)) return null
    visto.add(atual.id)
    if (atual.parentId === parentId) return atual
    atual = atual.parentId === null ? undefined : byId.get(atual.parentId)
  }
  return null
}

/**
 * Ordena, calcula o percentual e junta as fatias pequenas em "Outros".
 *
 * **"Outros" só existe com DUAS ou mais fatias pequenas.** Uma fatia de 3%
 * rebatizada de "Outros" perderia o nome dela sem juntar nada — o leitor deixa
 * de saber o que é e não ganha simplicidade em troca.
 */
function colapsar(somas: Map<string | null, { name: string; cents: number }>, total: number): Shares {
  if (total === 0) return { totalCents: 0, slices: [] }

  const todas: Slice[] = [...somas.entries()]
    .map(([id, { name, cents }]) => ({
      id,
      name,
      cents,
      percent: Math.round((cents / total) * 1000) / 10,
    }))
    .sort((a, b) => b.cents - a.cents)

  const grandes = todas.filter((slice) => slice.percent >= CORTE)
  const pequenas = todas.filter((slice) => slice.percent < CORTE)
  if (pequenas.length < 2) return { totalCents: total, slices: todas }

  const cents = pequenas.reduce((soma, slice) => soma + slice.cents, 0)
  return {
    totalCents: total,
    slices: [
      ...grandes,
      { id: 'outros', name: 'Outros', cents, percent: Math.round((cents / total) * 1000) / 10 },
    ],
  }
}
